import React, { useState, useEffect, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';
import styled from 'styled-components';
import { srConfig } from '@config';
import { KEY_CODES } from '@utils';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';
import { useLanguage } from '../../context/language';

const StyledJobsSection = styled.section`
  max-width: 920px;

  .inner {
    display: flex;
    gap: 24px;

    @media (max-width: 600px) {
      display: block;
    }

    @media (min-width: 700px) {
      min-height: 360px;
    }
  }
`;

const StyledTabList = styled.div`
  position: relative;
  z-index: 3;
  width: max-content;
  padding: 0;
  margin: 0;
  list-style: none;

  @media (max-width: 600px) {
    display: flex;
    overflow-x: auto;
    width: calc(100% + 100px);
    padding-left: 50px;
    margin-left: -50px;
    margin-bottom: 30px;
  }
  @media (max-width: 480px) {
    width: calc(100% + 50px);
    padding-left: 25px;
    margin-left: -25px;
  }

  li {
    &:first-of-type {
      @media (max-width: 600px) {
        margin-left: 50px;
      }
      @media (max-width: 480px) {
        margin-left: 25px;
      }
    }
    &:last-of-type {
      @media (max-width: 600px) {
        padding-right: 50px;
      }
      @media (max-width: 480px) {
        padding-right: 25px;
      }
    }
  }
`;

const StyledTabButton = styled.button`
  ${({ theme }) => theme.mixins.link};
  display: flex;
  align-items: center;
  width: 100%;
  height: var(--tab-height);
  padding: 0 18px 2px;
  border: 1px solid ${({ isActive }) => (isActive ? 'rgba(24, 34, 53, 0.02)' : 'var(--surface-border)')};
  border-left: 1px solid ${({ isActive }) => (isActive ? 'rgba(24, 34, 53, 0.02)' : 'var(--surface-border)')};
  border-radius: 16px;
  background-color: ${({ isActive }) => (isActive ? 'var(--lightest-slate)' : 'var(--surface-strong)')};
  color: ${({ isActive }) => (isActive ? 'var(--white)' : 'var(--slate)')};
  font-family: var(--font-mono);
  font-size: var(--fz-xs);
  text-align: left;
  white-space: nowrap;
  box-shadow: 0 16px 30px -28px var(--navy-shadow);

  @media (max-width: 768px) {
    padding: 0 15px 2px;
  }
  @media (max-width: 600px) {
    ${({ theme }) => theme.mixins.flexCenter};
    min-width: max-content;
    padding: 0 15px;
    border-bottom: 1px solid ${({ isActive }) =>
    isActive ? 'rgba(24, 34, 53, 0.02)' : 'var(--surface-border)'};
    text-align: center;
  }

  &:hover,
  &:focus {
    background-color: ${({ isActive }) => (isActive ? 'var(--lightest-slate)' : 'var(--surface-hover)')};
  }
`;

const StyledHighlight = styled.div`
  display: none;
`;

const StyledTabPanels = styled.div`
  position: relative;
  width: 100%;
  margin-left: 8px;
  padding: 24px 28px;
  border-radius: 28px;
  background: var(--surface-strong);
  border: 1px solid var(--surface-border);
  box-shadow: 0 24px 40px -34px var(--navy-shadow);

  @media (max-width: 600px) {
    margin-left: 0;
    padding: 22px 20px;
  }
`;

const StyledTabPanel = styled.div`
  width: 100%;
  height: auto;
  padding: 10px 5px;

  ul {
    ${({ theme }) => theme.mixins.fancyList};
  }

  h3 {
    margin-bottom: 2px;
    font-size: var(--fz-xxl);
    font-weight: 500;
    line-height: 1.3;

    .company {
      color: var(--green);
    }
  }

  .range,
  .location {
    color: var(--light-slate);
    font-family: var(--font-mono);
    font-size: var(--fz-xs);
    margin: 0;
  }

  .range {
    margin-top: 8px;
  }

  .location {
    margin-bottom: 24px;
  }
`;

const Jobs = () => {
  const { content } = useLanguage();
  const jobsData = content.jobs.items;

  const [activeTabId, setActiveTabId] = useState(0);
  const [tabFocus, setTabFocus] = useState(null);
  const tabs = useRef([]);
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, [prefersReducedMotion]);

  useEffect(() => {
    setActiveTabId(0);
    setTabFocus(0);
  }, [content]);

  const focusTab = () => {
    if (tabs.current[tabFocus]) {
      tabs.current[tabFocus].focus();
      return;
    }

    if (tabFocus >= tabs.current.length) {
      setTabFocus(0);
    }

    if (tabFocus < 0) {
      setTabFocus(tabs.current.length - 1);
    }
  };

  useEffect(() => focusTab(), [tabFocus]);

  const onKeyDown = e => {
    switch (e.key) {
      case KEY_CODES.ARROW_UP: {
        e.preventDefault();
        setTabFocus(tabFocus - 1);
        break;
      }

      case KEY_CODES.ARROW_DOWN: {
        e.preventDefault();
        setTabFocus(tabFocus + 1);
        break;
      }

      default: {
        break;
      }
    }
  };

  return (
    <StyledJobsSection id="jobs" ref={revealContainer}>
      <h2 className="numbered-heading">{content.jobs.heading}</h2>

      <div className="inner">
        <StyledTabList role="tablist" aria-label={content.jobs.tabsAriaLabel} onKeyDown={e => onKeyDown(e)}>
          {jobsData.map(({ company }, i) => (
            <StyledTabButton
              key={i}
              isActive={activeTabId === i}
              onClick={() => setActiveTabId(i)}
              ref={el => (tabs.current[i] = el)}
              id={`tab-${i}`}
              role="tab"
              tabIndex={activeTabId === i ? '0' : '-1'}
              aria-selected={activeTabId === i}
              aria-controls={`panel-${i}`}>
              <span>{company}</span>
            </StyledTabButton>
          ))}
          <StyledHighlight activeTabId={activeTabId} />
        </StyledTabList>

        <StyledTabPanels>
          {jobsData.map((job, i) => {
            const { title, url, company, range, location, points } = job;

            return (
              <CSSTransition key={i} in={activeTabId === i} timeout={250} classNames="fade">
                <StyledTabPanel
                  id={`panel-${i}`}
                  role="tabpanel"
                  tabIndex={activeTabId === i ? '0' : '-1'}
                  aria-labelledby={`tab-${i}`}
                  aria-hidden={activeTabId !== i}
                  hidden={activeTabId !== i}>
                  <h3>
                    <span>{title}</span>
                    <span className="company">
                      &nbsp;@&nbsp;
                      <a href={url} className="inline-link">
                        {company}
                      </a>
                    </span>
                  </h3>

                  <p className="range">{range}</p>
                  <p className="location">{location}</p>

                  <ul>
                    {points.map((point, pointIndex) => (
                      <li key={pointIndex}>{point}</li>
                    ))}
                  </ul>
                </StyledTabPanel>
              </CSSTransition>
            );
          })}
        </StyledTabPanels>
      </div>
    </StyledJobsSection>
  );
};

export default Jobs;
